import React, {
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { AppContext } from '@prisjakt/ui-app-context';

import Breadcrumb from '@prisjakt/ui-breadcrumb';
import PageContent from '../../../../components/page-content';
import ShopSearch from '../../../../components/shop-search';

import Header from './components/header';
import RatedShops from './components/rated-shops';
import TabNavigation from '../../../../components/sticky-tab-nav';
import Articles from './components/articles';
import FeaturedArticle from './components/articles/featured';

import useReviewData from '../../../reviews/hooks/data';
import { graphql } from '../../../../globals/api/requests';
import genericUserReview from '../../../reviews/generic-user-review.graphql';

import { ShopOfTheYear, PrismicArticle } from '../../../../types';
import { UserReview } from '../../../reviews/types';

import * as S from './styles';

type Props = {
  data: {
    shopOfTheYear: ShopOfTheYear;
    latestReviews: UserReview[];
    prismicArticles: {
      nodes: PrismicArticle[];
    };
  };
  routeSection?: string;
};

type SearchItem = {
  id: number;
  name: string;
  semantic_id: string; // eslint-disable-line camelcase
};

enum TabAnchor {
  WINNERS = '#winners',
  SEARCH = '#search',
  ARTICLES = '#articles',
}

const INITIAL_OFFSET = 20;

const ShopOfTheYearLandingPage = ({
  data,
  routeSection: winnerYear,
}: Props) => {
  const { categories, charity } = data.shopOfTheYear;
  const { captureException, linkTo, market, renderMode, t } = useContext(
    AppContext
  );
  const { latestReviews, prismicArticles } = data;
  const { push } = useHistory();
  const { reviews } = useReviewData({
    initialNodes: latestReviews,
    pageType: 'shopOfTheYear',
  });
  const shouldFetch = useRef(true);
  const offset = useRef(INITIAL_OFFSET);

  const [shops, setShops] = useState(() =>
    reviews
      .map(review => review.store)
      // Filter out duplicates
      .filter(
        (shop, index, self) =>
          self.map(currShop => currShop?.name).indexOf(shop?.name) === index
      )
      .slice(0, 10)
  );

  const nativeMode = renderMode === 'native';

  useEffect(() => {
    // Keep fetching more `latestReviews` until the list has 10 shops
    if (shops.length && shops.length < 10 && shouldFetch.current) {
      graphql(
        `
          query getLatestReviews(
            $getProduct: Boolean = false
            $getStore: Boolean = true
            $offset: Int
            $limit: Int
          ) {
            latestReviews(reviewType: STORES, limit: $limit, offset: $offset) {
              ...GenericUserReview
            }
          }
          ${genericUserReview}
        `,
        { offset: offset.current, limit: INITIAL_OFFSET }
      )
        .then(response => {
          if (response.data && !response.errors) {
            const moreShops = [
              ...shops,
              ...response.data.latestReviews.map(
                (review: UserReview) => review.store
              ),
            ]
              .filter(
                (shop, index, self) =>
                  self.map(curr => curr.name).indexOf(shop.name) === index
              )
              .slice(0, 10);

            setShops(moreShops);
            offset.current += INITIAL_OFFSET;
          } else {
            captureException(response.errors);
            shouldFetch.current = false;
          }
        })
        .catch(err => {
          captureException(err);
          shouldFetch.current = false;
        });
    } else {
      // Make sure we stop fetching more data if shops > 10
      shouldFetch.current = false;
    }
  }, [shops]);

  const currentYear = market.shopOfTheYear?.year;

  const cdnDir = `${market.cdn}/g/store_of_the_year/${currentYear}/${market.code}`;
  const badgeURI = `${cdnDir}/award/main_award.png`;

  const featuredArticle = prismicArticles?.nodes.find(article =>
    article.tags.includes('featured')
  );

  const handleSelectSearchItem = useCallback(
    (shop: SearchItem) => {
      if (nativeMode) {
        window.location.href = linkTo('store', shop, { review: 'write' });
      } else {
        push(linkTo('store', shop, { review: 'write' }));
      }
    },
    [linkTo, push, nativeMode]
  );

  return (
    <PageContent withAds>
      {nativeMode && (
        <Breadcrumb
          homeText={t('common', 'breadcrumb.home')}
          path={[
            {
              pathName: linkTo('shopOfTheYear'),
              name: t('campaigns', 'shop-of-the-year.title'),
              id: '1',
            },
          ]}
        />
      )}
      <S.SotyPage>
        <Header
          mainBadgeURI={badgeURI}
          charity={charity}
          currentYear={currentYear}
        />
        <TabNavigation
          defaultActiveTabId={TabAnchor.SEARCH}
          tabs={[
            {
              id: TabAnchor.SEARCH,
              title: t('campaigns', 'shop-of-the-year.tabs-contest'),
            },
            {
              id: TabAnchor.ARTICLES,
              title: t('campaigns', 'shop-of-the-year.tabs-articles'),
            },
            {
              id: TabAnchor.WINNERS,
              title: t('campaigns', 'shop-of-the-year.tabs-winners'),
            },
          ]}
        />

        <S.Search id={TabAnchor.SEARCH}>
          <ShopSearch onSelect={handleSelectSearchItem} />
        </S.Search>

        {featuredArticle ? (
          <>
            <S.SectionTitle />
            <FeaturedArticle article={featuredArticle} />
          </>
        ) : null}

        <RatedShops reviews={reviews} shops={shops} />

        {prismicArticles?.nodes?.length ? (
          <div id={TabAnchor.ARTICLES}>
            <S.SectionTitle>
              {t('campaigns', 'shop-of-the-year.tabs-articles')}
            </S.SectionTitle>
            <Articles nodes={prismicArticles.nodes} />
          </div>
        ) : null}

        {currentYear ? (
          <S.WinnersSection id={TabAnchor.WINNERS}>
            <S.SectionTitle>
              {t('campaigns', 'shop-of-the-year.winners')}
            </S.SectionTitle>
            <S.Winners
              categories={categories}
              currentYear={currentYear}
              winnerYear={winnerYear}
            />
          </S.WinnersSection>
        ) : null}
      </S.SotyPage>
    </PageContent>
  );
};

export default ShopOfTheYearLandingPage;
